// Ubicación en el repo: src/components/Layout.jsx
// Cascarón de las pantallas internas: header con selector de sucursal
// activa, toggle de módulo (ORL/OFT) y sesión — más un <Outlet> que le
// pasa ese estado a la pantalla actual vía useOutletContext(). Mismo
// concepto que activeBranch/activeModule del prototipo (spec-prototipo.md
// 3.6), pero acá es solo UX: el filtrado de seguridad real sigue siendo
// RLS, no este selector.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useSession } from '../lib/SessionContext';
import { useCurrentProfessional } from '../lib/ProfessionalContext';
import { listActiveBranches, isModuleEnabledForBranch } from '../lib/branches';
import { getBranchLogoUrl } from '../lib/branchLogos';
import { getClinicSettings, applyClinicSettings } from '../lib/clinicSettings';
import { logout, attachInactivityLock } from '../lib/auth';
import Spinner from './Spinner';

export default function Layout() {
  const { session } = useSession();
  const { status: profStatus, professional, branchIds } = useCurrentProfessional();
  const navigate = useNavigate();

  const [allBranches, setAllBranches] = useState([]);
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const [branchesError, setBranchesError] = useState('');
  const [activeBranchId, setActiveBranchId] = useState(
    () => localStorage.getItem('activeBranchId') || ''
  );
  const [activeModule, setActiveModule] = useState(
    () => localStorage.getItem('activeModule') || 'ORL'
  );

  // BUG encontrado (reportado por el usuario): esto solo se cargaba una
  // vez al montar Layout, que vive fijo durante toda la sesión (no se
  // remonta al navegar entre pantallas, al ser el cascarón). Si un Admin
  // cambiaba algo de una sucursal en Configuración — el caso reportado:
  // deshabilitar un módulo — y volvía sin recargar la página entera, el
  // header seguía mostrando la copia vieja de `allBranches`, con ambos
  // módulos igual visibles. Se expone `refreshBranches` por el contexto
  // del Outlet para que Branches.jsx la llame después de guardar/activar/
  // desactivar, sin depender de un refresco completo del navegador.
  const refreshBranches = useCallback(() => {
    return listActiveBranches()
      .then(setAllBranches)
      .catch((err) => setBranchesError(err.message || 'No se pudieron cargar las sucursales.'))
      .finally(() => setBranchesLoaded(true));
  }, []);

  useEffect(() => {
    refreshBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apariencia (colores/modo oscuro) aplica para todos, no solo Admin —
  // igual que el prototipo, donde applySettings() corre para cualquier
  // usuario al cargar. Solo Admin puede cambiarla (RLS clinic_settings_write),
  // pero todos la ven aplicada. Si falla (ej. red), la app sigue con los
  // colores por defecto del CSS — no es bloqueante.
  useEffect(() => {
    getClinicSettings()
      .then(applyClinicSettings)
      .catch(() => {});
  }, []);

  // Sucursales que puede elegir este profesional: todas si es Admin o no
  // tiene ninguna asignada (= "todas", igual que has_branch_access()),
  // si no, solo las suyas.
  const selectableBranches = useMemo(() => {
    if (!professional) return [];
    if (professional.is_admin || branchIds.length === 0) return allBranches;
    return allBranches.filter((b) => branchIds.includes(b.id));
  }, [allBranches, professional, branchIds]);

  useEffect(() => {
    if (!branchesLoaded || selectableBranches.length === 0) return;
    const stillValid = selectableBranches.some((b) => b.id === activeBranchId);
    if (!stillValid) {
      setActiveBranchId(selectableBranches[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchesLoaded, selectableBranches]);

  useEffect(() => {
    if (activeBranchId) localStorage.setItem('activeBranchId', activeBranchId);
  }, [activeBranchId]);

  useEffect(() => {
    localStorage.setItem('activeModule', activeModule);
  }, [activeModule]);

  // Logo de la sucursal activa en el header, junto al banner "Agenda
  // Comunal" (DECISIONS.md #39 — quedó pendiente en la primera pasada,
  // que solo cubría los imprimibles). Se resuelve de nuevo cada vez que
  // cambia la sucursal activa, porque cada una puede tener un logo distinto.
  const activeBranch = selectableBranches.find((b) => b.id === activeBranchId) || null;
  const [headerLogoUrl, setHeaderLogoUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getBranchLogoUrl(activeBranch).then((url) => {
      if (!cancelled) setHeaderLogoUrl(url);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch?.logo_path]);

  // Módulos por sucursal (DECISIONS.md #42): si la sucursal activa no
  // ofrece el módulo actualmente seleccionado (ej. se cambió a una
  // sucursal solo-OFT estando en ORL), se salta automáticamente al que sí
  // tiene habilitado — evita quedar en una pantalla vacía/bloqueada por el
  // guard de cada pantalla de módulo.
  useEffect(() => {
    if (!activeBranch) return;
    if (isModuleEnabledForBranch(activeBranch, activeModule)) return;
    const fallback = isModuleEnabledForBranch(activeBranch, 'ORL')
      ? 'ORL'
      : isModuleEnabledForBranch(activeBranch, 'OFT')
        ? 'OFT'
        : null;
    if (fallback) setActiveModule(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch?.id, activeBranch?.orl_enabled, activeBranch?.oft_enabled]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  // Bloqueo por inactividad (checklist sección 2): attachInactivityLock()
  // ya existía en lib/auth.js pero nunca se había enganchado a ningún
  // componente. Layout es el cascarón fijo de toda sesión autenticada — no
  // se remonta al navegar entre pantallas (ver comentario arriba, línea
  // ~35) — así que es el lugar correcto para engancharlo una sola vez por
  // sesión, en vez de repetirlo en cada pantalla. 30 minutos (no los 10 que
  // trae la función por defecto — con 2FA obligatorio para roles clínicos,
  // 10 min resultaba en pedir el código del autenticador varias veces por
  // hora en un mesón de recepción con huecos normales entre pacientes; 30
  // min sigue acotando bien la exposición sin esa fricción, ver DECISIONS.md
  // #54/#55). Al vencer, mismo flujo que el botón "Cerrar sesión": signOut +
  // redirigir a /login.
  useEffect(() => {
    const detach = attachInactivityLock(() => {
      handleLogout();
    }, 30 * 60_000);
    return detach;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = profStatus === 'loading' || profStatus === 'idle' || !branchesLoaded;

  if (loading) {
    return (
      <div className="app-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (profStatus === 'error') {
    return (
      <div className="app-loading">
        No se pudo cargar tu ficha de profesional. Contacta a un Admin.
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__brand">
          {headerLogoUrl && <img src={headerLogoUrl} alt="" className="app-header__logo" />}
          <div className="app-header__brand-text">
            <strong>Agenda Comunal</strong>
            <span className="app-header__sub">GlobalVision</span>
          </div>
        </div>

        <img src="/agendadock-logo.png" alt="AgendaDock" className="app-header__powered-logo" />

        <nav className="app-nav">
          <NavLink to="/app" end className={({ isActive }) => (isActive ? 'active' : '')}>
            Inicio
          </NavLink>
          <NavLink to="/app/pacientes" className={({ isActive }) => (isActive ? 'active' : '')}>
            Pacientes
          </NavLink>
          <NavLink to="/app/agenda" className={({ isActive }) => (isActive ? 'active' : '')}>
            Agenda
          </NavLink>
          {activeModule === 'ORL' && (
            <NavLink to="/app/audiometrias" className={({ isActive }) => (isActive ? 'active' : '')}>
              Audiometrías
            </NavLink>
          )}
          {activeModule === 'ORL' && (
            <NavLink to="/app/audifonos" className={({ isActive }) => (isActive ? 'active' : '')}>
              Audífonos
            </NavLink>
          )}
          {activeModule === 'OFT' && (
            <NavLink to="/app/recetas" className={({ isActive }) => (isActive ? 'active' : '')}>
              Recetas
            </NavLink>
          )}
          {activeModule === 'OFT' && (
            <NavLink to="/app/ventas" className={({ isActive }) => (isActive ? 'active' : '')}>
              Ventas
            </NavLink>
          )}
          {professional?.is_admin && (
            <NavLink to="/app/configuracion" className={({ isActive }) => (isActive ? 'active' : '')}>
              Configuración
            </NavLink>
          )}
        </nav>

        <div className="app-header__controls">
          <div className="module-toggle">
            {isModuleEnabledForBranch(activeBranch, 'ORL') && (
              <button
                type="button"
                className={activeModule === 'ORL' ? 'active' : ''}
                onClick={() => setActiveModule('ORL')}
              >
                ORL
              </button>
            )}
            {isModuleEnabledForBranch(activeBranch, 'OFT') && (
              <button
                type="button"
                className={activeModule === 'OFT' ? 'active' : ''}
                onClick={() => setActiveModule('OFT')}
              >
                OFT
              </button>
            )}
          </div>

          <select
            className="branch-select"
            value={activeBranchId}
            onChange={(e) => setActiveBranchId(e.target.value)}
          >
            {selectableBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="app-header__user">
          <span>
            {professional?.nombre} {professional?.apellido}
            {professional?.is_admin ? ' · Admin' : ''}
          </span>
          <button type="button" className="link-button" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </header>

      {branchesError && <div className="app-banner-error">{branchesError}</div>}

      <main className="app-main">
        <Outlet
          context={{
            session,
            professional,
            activeBranchId,
            activeBranch,
            activeModule,
            selectableBranches,
            refreshBranches,
          }}
        />
      </main>

      {/* Contenedor de impresión (comprobantes, agenda del día) — oculto
          en pantalla, visible solo dentro de @media print (ver index.css).
          Mismo patrón que #printArea del prototipo. */}
      <div id="print-area" />
    </div>
  );
}
